// Skeleton Loading Components
// Premium skeleton animations for better UX

import * as React from 'react';
import { Box, Skeleton as MuiSkeleton, styled, Theme } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';

// Styled Components
const StyledSkeleton = styled(MuiSkeleton, {
  shouldForwardProp: (prop) => !['shimmer', 'wave', 'pulse', 'type'].includes(prop as string),
})<{
  shimmer?: boolean;
  wave?: boolean;
  pulse?: boolean;
  type?: 'text' | 'rectangular' | 'circular' | 'rounded';
}>(({ theme, shimmer = true, wave = false, pulse = false, type = 'text' }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1E293B' : '#E2E8F0',
  borderRadius: type === 'rounded' ? theme.shape.borderRadius * 2 : type === 'circular' ? 9999 : theme.shape.borderRadius,
  ...(type === 'circular' && { width: 40, height: 40, borderRadius: '50%' }),
  ...(type === 'rectangular' && { width: '100%', height: 200 }),
  ...(type === 'text' && { height: 12, marginTop: 4 }),
  ...(shimmer && {
    background: `linear-gradient(90deg, ${theme.palette.mode === 'dark' ? '#1E293B' : '#E2E8F0'} 25%, ${theme.palette.mode === 'dark' ? '#334155' : '#CBD5E1'} 50%, ${theme.palette.mode === 'dark' ? '#1E293B' : '#E2E8F0'} 75%)`,
    backgroundSize: '200% 100%',
    animation: wave ? 'wave 2s ease-in-out infinite' : 'shimmer 1.5s ease-in-out infinite',
  }),
  ...(pulse && {
    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
  }),
  '@keyframes shimmer': {
    '0%': { backgroundPosition: '200% 0' },
    '100%': { backgroundPosition: '-200% 0' },
  },
  '@keyframes wave': {
    '0%': { backgroundPosition: '200% 0', opacity: 0.5 },
    '50%': { backgroundPosition: '0% 0', opacity: 1 },
    '100%': { backgroundPosition: '-200% 0', opacity: 0.5 },
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 0.5 },
    '50%': { opacity: 1 },
  },
}));

// Avatar Skeleton
export const AvatarSkeleton = ({ size = 'medium' }: { size?: 'small' | 'medium' | 'large' }) => {
  const sizes = {
    small: { width: 32, height: 32 },
    medium: { width: 40, height: 40 },
    large: { width: 48, height: 48 },
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      style={sizes[size]}
    >
      <StyledSkeleton variant="circular" shimmer={true} wave={false} pulse={false} />
    </motion.div>
  );
};

// Text Skeleton
interface TextSkeletonProps {
  lines?: number;
  width?: string | number;
  height?: string | number;
  className?: string;
}

export const TextSkeleton = ({ lines = 1, width = '100%', height = 16, className }: TextSkeletonProps) => {
  return (
    <Box className={className} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {Array.from({ length: lines }).map((_, i) => (
        <StyledSkeleton
          key={i}
          variant="text"
          width={i === lines - 1 ? width : undefined}
          height={height}
          shimmer={true}
          wave={true}
          style={{ borderRadius: 4 }}
        />
      ))}
    </Box>
  );
};

// Card Skeleton
export const CardSkeleton = ({ variant = 'default' }: { variant?: 'default' | 'large' | 'horizontal' | 'compact' }) => {
  const variants = {
    default: { aspectRatio: '2/3', minWidth: 160, maxWidth: 220 },
    large: { aspectRatio: '2/3', minWidth: 200, maxWidth: 280 },
    horizontal: { aspectRatio: '16/9', minWidth: 320, maxWidth: '100%' },
    compact: { aspectRatio: '1/1.4', minWidth: 120, maxWidth: 160 },
  };

  const v = variants[variant];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      style={{ minWidth: v.minWidth, maxWidth: v.maxWidth }}
    >
      <Box
        sx={{
          borderRadius: 16,
          overflow: 'hidden',
          backgroundColor: 'var(--anime-token-surface-default)',
          border: '1px solid var(--anime-token-border-default)',
        }}
      >
        <StyledSkeleton
          variant="rectangular"
          width="100%"
          style={{ aspectRatio: v.aspectRatio, borderRadius: 0 }}
          shimmer={true}
          wave={true}
          pulse={false}
        />
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextSkeleton lines={2} width="80%" height={16} />
          <TextSkeleton lines={1} width="60%" height={12} />
          <TextSkeleton lines={1} width="40%" height={12} />
        </Box>
      </Box>
    </motion.div>
  );
};

// Hero Card Skeleton
export const HeroSkeleton = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    style={{ width: '100%', aspectRatio: '16/9', borderRadius: 24, overflow: 'hidden' }}
  >
    <StyledSkeleton
      variant="rectangular"
      width="100%"
      height="100%"
      shimmer={true}
      style={{ borderRadius: 0 }}
    />
    <Box
      sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        padding: 32,
        background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      <TextSkeleton lines={2} width="40%" height={32} />
      <TextSkeleton lines={2} width="60%" height={20} />
      <Box sx={{ display: 'flex', gap: 16 }}>
        <StyledSkeleton variant="rectangular" width={160} height={48} shimmer wave />
        <StyledSkeleton variant="rectangular" width={140} height={48} shimmer wave />
      </Box>
    </Box>
  </motion.div>
);

// Feature Card Skeleton
export const FeatureCardSkeleton = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    style={{ height: '100%' }}
  >
    <Box
      sx={{
        borderRadius: 16,
        overflow: 'hidden',
        backgroundColor: 'var(--anime-token-surface-default)',
        border: '1px solid var(--anime-token-border-default)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <StyledSkeleton
        variant="rectangular"
        width="100%"
        height={200}
        shimmer={true}
        wave={true}
        style={{ borderRadius: 0 }}
      />
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
        <TextSkeleton lines={3} width="100%" height={16} />
        <TextSkeleton lines={2} width="70%" height={14} />
        <Box sx={{ mt: 'auto', display: 'flex', gap: 8 }}>
          <StyledSkeleton variant="rounded" width={60} height={24} shimmer wave />
          <StyledSkeleton variant="rounded" width={60} height={24} shimmer wave />
        </Box>
      </Box>
    </Box>
  </motion.div>
);

// List Item Skeleton
interface ListItemSkeletonProps {
  showAvatar?: boolean;
  showSubtitle?: boolean;
  dense?: boolean;
}

export const ListItemSkeleton = ({ showAvatar = false, showSubtitle = true, dense = false }: ListItemSkeletonProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
      style={{ display: 'flex', alignItems: 'center', width: '100%', padding: dense ? 8 : 16 }}
    >
      {showAvatar && (
        <StyledSkeleton
          variant="circular"
          width={dense ? 32 : 40}
          height={dense ? 32 : 40}
          shimmer={true}
          wave={true}
          style={{ marginRight: dense ? 12 : 16, flexShrink: 0 }}
        />
      )}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: dense ? 4 : 8 }}>
        <TextSkeleton lines={1} width={dense ? '60%' : '80%'} height={dense ? 14 : 16} />
        {showSubtitle && <TextSkeleton lines={1} width={dense ? '40%' : '50%'} height={dense ? 12 : 14} />}
      </Box>
    </motion.div>
  );
};

// Table Row Skeleton
export const TableRowSkeleton = ({ columns = 4 }: { columns?: number }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      style={{ display: 'flex', alignItems: 'center', width: '100%', padding: 16, borderBottom: '1px solid var(--anime-token-divider)' }}
    >
      {Array.from({ length: columns }).map((_, index) => (
        <Box key={index} sx={{ flex: 1, px: 8 }}>
          <TextSkeleton lines={index === 0 ? 2 : 1} width="80%" height={16} />
        </Box>
      ))}
    </motion.div>
  );
};

// Profile Skeleton
export const ProfileSkeleton = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    style={{ width: '100%', maxWidth: 800, mx: 'auto' }}
  >
    <Box sx={{ textAlign: 'center', mb: 6 }}>
      <StyledSkeleton
        variant="circular"
        width={120}
        height={120}
        shimmer={true}
        wave={true}
        style={{ mx: 'auto', mb: 3 }}
      />
      <TextSkeleton lines={2} width="30%" height={20} style={{ mx: 'auto' }} />
    </Box>

    <Box sx={{ display: 'flex', gap: 3, mb: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
      {Array.from({ length: 4 }).map((_, i) => (
        <Box key={i} sx={{ width: 120, textAlign: 'center' }}>
          <StyledSkeleton variant="rectangular" width={80} height={40} shimmer wave style={{ mb: 2 }} />
          <TextSkeleton lines={1} width="100%" height={12} />
        </Box>
      ))}
    </Box>

    <Box sx={{ mt: 4 }}>
      <TextSkeleton lines={3} width="100%" height={16} />
    </Box>
  </motion.div>
);

// Detail Page Skeleton
export const DetailSkeleton = () => (
  <Box sx={{ width: '100%' }}>
    {/* Backdrop */}
    <Box sx={{ width: '100%', height: { xs: 260, sm: 360, md: 480 }, backgroundColor: 'var(--anime-token-surface-variant)' }} />
    {/* Content */}
    <Box sx={{ display: 'flex', gap: 4, mt: { xs: -18, md: -24 }, flexDirection: { xs: 'column', md: 'row' } }}>
      {/* Poster */}
      <Box sx={{ flexShrink: 0, width: { md: 280 }, pl: { md: 3 } }}>
        <StyledSkeleton variant="rectangular" width="100%" height={420} shimmer wave />
      </Box>
      {/* Info */}
      <Box sx={{ flex: 1, pr: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <StyledSkeleton variant="rounded" width={60} height={24} shimmer />
          <StyledSkeleton variant="rounded" width={80} height={24} shimmer />
        </Box>
        <TextSkeleton lines={1} width="60%" height={48} />
        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <StyledSkeleton variant="rounded" width={80} height={28} shimmer />
          <StyledSkeleton variant="rounded" width={50} height={28} shimmer />
        </Box>
        <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
          {[1, 2, 3, 4].map(i => (
            <StyledSkeleton key={i} variant="rounded" width={70} height={28} shimmer />
          ))}
        </Box>
        <TextSkeleton lines={4} width="100%" height={16} />
      </Box>
    </Box>
  </Box>
);

export default Skeleton;